import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import AlumniLayout from "@/components/AlumniLayout";
import ClassFaculty from "@/components/ClassFaculty";
import { Users, MapPin, Briefcase, ArrowLeft, Linkedin, Facebook, Twitter, Instagram, Globe, GraduationCap, Send, X, ShieldCheck, Search } from "lucide-react";
import { useUiState } from "@/lib/uiState";

const SOCIAL_ICONS = { linkedin: Linkedin, facebook: Facebook, twitter: Twitter, instagram: Instagram, website: Globe };

const DEFAULT_NOTE = "Hey — found you on the new UD Alumni Association page and thought I would reach out and say hi. Hard to believe how long it's been. Feel free to let me know what you have been up to!";

export default function AlumniDirectory() {
  const [params, setParams] = useSearchParams();
  const yearParam = params.get("year");
  const [classes, setClasses] = useState([]);
  const [year, setYear] = useState(yearParam ? parseInt(yearParam, 10) : null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useUiState("ud_alumnidir_sort", "first");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/experience/classes").then((r) => setClasses(r.data.classes)).catch(() => {});
  }, []);

  useEffect(() => {
    if (year == null) { setMembers([]); return; }
    setLoading(true);
    api.get(`/experience/classes/${year}`).then((r) => setMembers(r.data.members)).catch(() => {}).finally(() => setLoading(false));
  }, [year]);

  useEffect(() => {
    if (year != null) return;
    const term = query.trim();
    if (term.length < 2) { setMembers([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/experience/search?q=${encodeURIComponent(term)}`).then((r) => setMembers(r.data.members)).catch(() => {}).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, year]);

  const openYear = (y) => { setQuery(""); setYear(y); setParams({ year: String(y) }); };
  const back = () => { setQuery(""); setYear(null); setParams({}); };

  const { user } = useAuth();
  const [reach, setReach] = useState(null); // target member
  const [note, setNote] = useState(DEFAULT_NOTE);
  const [sending, setSending] = useState(false);
  const firstName = (m) => (m?.name || "there").trim().split(" ")[0];
  const lastName = (m) => { const p = (m?.name || "").trim().split(/\s+/); return p.length > 1 ? p[p.length - 1] : (p[0] || ""); };
  const sortedMembers = [...members].sort((a, b) => (sortBy === "last" ? lastName(a) : (a.name || "")).toLowerCase().localeCompare((sortBy === "last" ? lastName(b) : (b.name || "")).toLowerCase()));
  const q = query.trim().toLowerCase();
  const filtered = q ? sortedMembers.filter((m) => (m.search || (m.name || "").toLowerCase()).includes(q)) : sortedMembers;
  const letterOf = (m) => (((sortBy === "last" ? lastName(m) : (m.name || "")).trim()[0]) || "").toUpperCase();
  const letterFirstId = {};
  filtered.forEach((m) => { const L = letterOf(m); if (L && !letterFirstId[L]) letterFirstId[L] = m.id; });
  const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const jump = (L) => { const id = letterFirstId[L]; if (id) document.getElementById(`dir-card-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const openReach = (m) => { setReach(m); setNote(DEFAULT_NOTE); };
  const sendReach = async () => {
    if (!note.trim()) { toast.error("Please write a short message."); return; }
    setSending(true);
    try {
      await api.post(`/experience/alumni/${reach.id}/reach-out`, { message: note });
      toast.success(`Your message was sent to ${firstName(reach)} — we kept both emails private.`);
      setReach(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send your message.");
    } finally { setSending(false); }
  };

  return (
    <AlumniLayout wide>
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Class Directory</div>
        <h1 className="font-serif text-3xl tracking-tight mt-1">{year ? `Class of ${year}` : "Every Class of Crusaders"}</h1>
      </div>
      <div className="relative max-w-md mb-6">
        <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="dir-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={year ? "Search this class by name…" : "Search all alumni by name…"} className="w-full pl-9 pr-3 py-2 rounded-md border border-input bg-background text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
      </div>

      {year == null && !query.trim() && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {classes.map((c) => (
            <button key={c.class_year} data-testid={`dir-class-${c.class_year}`} onClick={() => openYear(c.class_year)} className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary hover:shadow-md transition-all group">
              <div className="flex items-center gap-2 text-primary"><GraduationCap className="h-5 w-5" /><span className="font-serif text-xl">{c.class_year}</span></div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2"><Users className="h-3.5 w-3.5" /> {c.count} alumni</div>
            </button>
          ))}
          {classes.length === 0 && <div className="text-muted-foreground col-span-full">No classes yet.</div>}
        </div>
      )}

      {(year != null || query.trim()) && (
        <div>
          {year != null && <button data-testid="dir-back" onClick={back} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-4"><ArrowLeft className="h-4 w-4" /> All classes</button>}
          {year != null && <ClassFaculty year={year} />}
          {!loading && members.length > 0 && (
            <div className="mb-5 space-y-3">
              <div className="flex items-center gap-2" data-testid="dir-sort">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Sort by</span>
                <button data-testid="sort-first" onClick={() => setSortBy("first")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sortBy === "first" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>First name</button>
                <button data-testid="sort-last" onClick={() => setSortBy("last")} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${sortBy === "last" ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:border-primary"}`}>Last name</button>
              </div>
              <div className="flex flex-wrap gap-1" data-testid="dir-alpha">
                {ALPHA.map((L) => {
                  const has = !!letterFirstId[L];
                  return (
                    <button key={L} data-testid={`alpha-${L}`} onClick={() => jump(L)} disabled={!has} title={has ? `Jump to ${L}` : undefined}
                      className={`h-7 w-7 rounded text-xs font-semibold transition-colors ${has ? "text-primary hover:bg-primary hover:text-white" : "text-muted-foreground/25 cursor-default"}`}>{L}</button>
                  );
                })}
              </div>
            </div>
          )}
          {loading && <div className="text-muted-foreground">Loading classmates…</div>}
          {!loading && members.length === 0 && <div className="text-muted-foreground" data-testid="dir-empty">No alumni listed for this class yet.</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((m) => (
              <div key={m.id} id={`dir-card-${m.id}`} data-testid={`dir-member-${m.id}`} className="bg-card border border-border rounded-lg shadow-sm p-5 scroll-mt-24 flex flex-col h-full">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-secondary text-white flex items-center justify-center overflow-hidden shrink-0">
                    {m.photo ? <img src={fileUrl(m.photo)} alt={m.name} className="h-full w-full object-cover" /> : <span className="font-serif text-lg">{(m.name || "?").slice(0, 1)}</span>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{m.name || "—"}</div>
                    {m.former_name && <div className="text-xs text-muted-foreground italic truncate" data-testid="member-maiden">née {m.former_name}</div>}
                    <div className="text-xs text-muted-foreground">Class of {m.class_year}</div>
                  </div>
                </div>
                {(m.title || m.employer) && <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-3"><Briefcase className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{[m.title, m.employer].filter(Boolean).join(" · ")}</span></div>}
                {(m.city || m.state) && <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1"><MapPin className="h-3.5 w-3.5 shrink-0" />{[m.city, m.state].filter(Boolean).join(", ")}</div>}
                {m.bio && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{m.bio}</p>}
                <div className="mt-auto pt-3">
                  <div className="flex gap-2">
                    {Object.entries(SOCIAL_ICONS).map(([k, Icon]) => (m.social?.[k] ? (
                      <a key={k} href={m.social[k]} target="_blank" rel="noreferrer" data-testid={`dir-social-${m.id}-${k}`} className="text-muted-foreground hover:text-primary"><Icon className="h-4 w-4" /></a>
                    ) : null))}
                  </div>
                  {m.has_email && (
                    <button data-testid={`dir-sayhi-${m.id}`} onClick={() => openReach(m)} className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-md border border-primary/40 text-primary text-sm font-medium hover:bg-primary/5 transition-colors">
                      <Send className="h-3.5 w-3.5" /> Say hi
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          {!loading && q && filtered.length === 0 && <div className="text-muted-foreground text-sm mt-3" data-testid="dir-noresults">No classmates match “{query}”.</div>}
        </div>
      )}
      {reach && createPortal((
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-slate-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) setReach(null); }}>
          <div className="bg-card w-full max-w-lg rounded-lg shadow-xl border border-border flex flex-col" style={{ maxHeight: "calc(100vh - 2rem)", overflow: "hidden" }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="min-w-0">
                <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Say hi — privately</div>
                <h2 className="font-serif text-lg tracking-tight truncate">To {reach.name}</h2>
              </div>
              <button data-testid="sayhi-close" onClick={() => setReach(null)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground shrink-0"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 overflow-auto space-y-4">
              <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/40 rounded-md p-3">
                <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>We send this for you — {reach.name.split(" ")[0]} never sees your email unless you include it. Your reply-to address <b className="text-foreground">{user?.email}</b> is added so they can write back. You can reach out to the same classmate once a year, and they can reply, ignore, or block right from the message.</span>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Your message</label>
                <textarea data-testid="sayhi-message" value={note} onChange={(e) => setNote(e.target.value)} rows={4} className="w-full px-3 py-2 rounded-md border border-input bg-background focus:ring-2 focus:ring-accent focus:outline-none resize-y" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Preview</div>
                <div data-testid="sayhi-preview" className="border border-border rounded-md p-3 text-sm bg-background whitespace-pre-wrap text-foreground/90">
                  {`Hi ${firstName(reach)},\n\n${note}\n\n— ${user?.name || "A fellow alumnus"}\nYou can reply directly to ${user?.email || ""}.`}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-border flex justify-end gap-2 shrink-0">
              <button onClick={() => setReach(null)} className="px-4 py-2.5 rounded-md border border-border text-sm hover:bg-muted">Cancel</button>
              <button data-testid="sayhi-send" onClick={sendReach} disabled={sending} className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-60">
                <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send message"}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </AlumniLayout>
  );
}
